/**
 * ConfigEmailPage
 *
 * Admin page to configure the outbound email service for this org.
 * Reads/writes organisation/{org_id}/config/email in Firestore via
 * the /admin/config/email endpoints.
 *
 * Route: /settings/config/email  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, CompactButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import { getServiceConfig, updateServiceConfig, getServiceCredential, updateServiceCredential, resetServiceCredential } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";
import { EMAIL_ADAPTER_OPTIONS } from "./configAdapterOptions";

const EMPTY_FORM = {
  adapter_type: "noop",
  from_address: "",
  from_name: "",
  region: "us-east-1",
};

const ConfigEmailPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
  const [credentialStatus, setCredentialStatus] = useState({ configured: false });
  const [credentialDraft, setCredentialDraft] = useState({ credential_key: "", credential_secret: "" });
  const [credentialAction, setCredentialAction] = useState("");
  const [editingCredential, setEditingCredential] = useState(false);
  const [confirmingSave, setConfirmingSave] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("email");
      if (data.configured) {
        const loaded = {
          adapter_type: data.adapter_type ?? "noop",
          from_address: data.from_address ?? "",
          from_name:    data.from_name    ?? "",
          region:       data.region       ?? "us-east-1",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load email config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const fetchCredentialStatus = useCallback(async () => {
    const data = await getServiceCredential("email");
    setCredentialStatus(data);
    return data;
  }, []);

  useEffect(() => {
    if (orgAvailable) fetchCredentialStatus();
  }, [orgAvailable, fetchCredentialStatus]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const discardDrafts = () => {
    setForm(originalForm);
    setCredentialDraft({ credential_key: "", credential_secret: "" });
    setCredentialAction("");
    setEditingCredential(false);
  };

  const persistChanges = async () => {
    setSaving(true);
    try {
      if (isDirty(form, originalForm)) {
        await updateServiceConfig("email", form);
      }
      if (credentialAction === "rotate") {
        await updateServiceCredential("email", credentialDraft);
      } else if (credentialAction === "revoke") {
        await resetServiceCredential("email");
      }
      setOriginalForm(form);
      setCredentialDraft({ credential_key: "", credential_secret: "" });
      setCredentialAction("");
      setEditingCredential(false);
      await fetchCredentialStatus();
      showFeedback("Email configuration saved and active.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save email config.", "error");
    } finally {
      setSaving(false);
      setConfirmingSave(false);
    }
  };

  const credentialsDirty = !!credentialAction;
  const pageDirty = isDirty(form, originalForm) || credentialsDirty;
  const handleSave = () => {
    if (credentialsDirty) {
      setConfirmingSave(true);
      return;
    }
    persistChanges();
  };

  return (
    <SettingsPageShell
      title="Email Configuration"
      description="Configure the outbound email adapter used for notifications and invitations."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          {/* Adapter type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Adapter
            </label>
            <Select
              value={form.adapter_type}
              onChange={(v) => setForm((prev) => ({ ...prev, adapter_type: v }))}
              options={EMAIL_ADAPTER_OPTIONS}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              No-op disables all outbound email sending. AWS SES requires verified sender identities in your AWS account.
            </p>
          </div>

          {/* From address */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              From Address
            </label>
            <Input
              type="email"
              placeholder="noreply@example.com"
              value={form.from_address}
              onChange={set("from_address")}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Sender address shown in outbound emails. Must be a verified identity in AWS SES for your region.
            </p>
          </div>

          {/* From name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              From Name
            </label>
            <Input
              type="text"
              placeholder="Photon AI"
              value={form.from_name}
              onChange={set("from_name")}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Display name shown to recipients alongside the From address.
            </p>
          </div>

          {/* AWS SES — region + credentials */}
          {form.adapter_type === "aws_ses" && (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  AWS Region
                </label>
                <Input
                  type="text"
                  placeholder="us-east-1"
                  value={form.region}
                  onChange={set("region")}
                />
                <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                  AWS region where your SES sender identity is verified (e.g. <code className="font-mono">us-east-1</code>).
                </p>
              </div>

              <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
                <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">AWS Credentials</p>
                {credentialAction === "revoke" ? (
                  <>
                    <p className="text-xs text-ink-red-600 dark:text-ink-red-400">Pending deletion. Save to revoke these credentials.</p>
                    <div><CompactButton onClick={() => setCredentialAction("")}>Undo</CompactButton></div>
                  </>
                ) : credentialAction === "rotate" ? (
                  <>
                    <p className="text-xs text-ink-amber-700 dark:text-ink-amber-400">Replacement staged. Save to activate these credentials.</p>
                    <div><CompactButton onClick={() => { setCredentialAction(""); setCredentialDraft({ credential_key: "", credential_secret: "" }); }}>Undo</CompactButton></div>
                  </>
                ) : editingCredential ? (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-ink-gray-700 dark:text-ink-gray-300">AWS Access Key ID</label>
                      <Input
                        placeholder="AKIAIOSFODNN7EXAMPLE"
                        value={credentialDraft.credential_key}
                        onChange={(event) => setCredentialDraft((previous) => ({ ...previous, credential_key: event.target.value }))}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs text-ink-gray-700 dark:text-ink-gray-300">AWS Secret Access Key</label>
                      <Input
                        type="password"
                        placeholder="Enter AWS secret access key"
                        value={credentialDraft.credential_secret}
                        onChange={(event) => setCredentialDraft((previous) => ({ ...previous, credential_secret: event.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <PrimaryButton
                        onClick={() => { setCredentialAction("rotate"); setEditingCredential(false); }}
                        disabled={!credentialDraft.credential_key.trim() || !credentialDraft.credential_secret.trim()}
                      >
                        Stage credentials
                      </PrimaryButton>
                      <CompactButton onClick={() => { setEditingCredential(false); setCredentialDraft({ credential_key: "", credential_secret: "" }); }}>
                        Cancel
                      </CompactButton>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                      {credentialStatus.configured
                        ? `${credentialStatus.credential_key || "Configured"}${credentialStatus.last4 ? ` / ••••${credentialStatus.last4}` : ""}`
                        : "Not configured."}
                    </p>
                    <div className="flex gap-2">
                      <CompactButton onClick={() => setEditingCredential(true)}>
                        {credentialStatus.configured ? "Rotate" : "Add credentials"}
                      </CompactButton>
                      {credentialStatus.configured && <CompactButton onClick={() => setCredentialAction("revoke")}>Revoke</CompactButton>}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !pageDirty}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
            <CompactButton onClick={discardDrafts} disabled={saving || !pageDirty}>Cancel</CompactButton>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmingSave}
        title="Save email changes?"
        confirmLabel="Save changes"
        loadingLabel="Saving…"
        confirmVariant={credentialAction === "revoke" ? "danger" : "primary"}
        isLoading={saving}
        onCancel={() => setConfirmingSave(false)}
        onConfirm={persistChanges}
      >
        {credentialAction === "revoke"
          ? "The AWS credentials will be deleted and outbound email will become unavailable immediately."
          : "The AWS credentials will be replaced and used immediately across API instances."}
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default ConfigEmailPage;
